import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  X,
  RefreshCw
} from 'lucide-react';
import { NOTIFICATION_TYPES } from '../../hooks/useNotifications';

const NotificationContainer = ({ notifications, onRemove, onAction }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  // Icone per i diversi tipi di notifica
  const getIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case NOTIFICATION_TYPES.ERROR:
        return <XCircle className="h-5 w-5 text-red-500" />;
      case NOTIFICATION_TYPES.WARNING:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case NOTIFICATION_TYPES.INFO:
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Colori per i diversi tipi di notifica
  const getColors = (type) => {
    const baseClasses = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return {
          container: `${baseClasses} border-l-4 border-l-green-500`,
          title: 'text-green-800 dark:text-green-200',
          message: 'text-green-700 dark:text-green-300'
        };
      case NOTIFICATION_TYPES.ERROR:
        return {
          container: `${baseClasses} border-l-4 border-l-red-500`,
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300'
        };
      case NOTIFICATION_TYPES.WARNING:
        return {
          container: `${baseClasses} border-l-4 border-l-yellow-500`,
          title: 'text-yellow-800 dark:text-yellow-200',
          message: 'text-yellow-700 dark:text-yellow-300'
        };
      case NOTIFICATION_TYPES.INFO:
      default:
        return {
          container: `${baseClasses} border-l-4 border-l-blue-500`,
          title: 'text-blue-800 dark:text-blue-200',
          message: 'text-blue-700 dark:text-blue-300'
        };
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm w-full">
      {notifications.map((notification) => {
        const colors = getColors(notification.type);
        
        return (
          <div
            key={notification.id}
            className={`${colors.container} rounded-lg shadow-lg border p-4 transform transition-all duration-300 ease-in-out animate-in slide-in-from-right`}
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {getIcon(notification.type)}
              </div>
              
              <div className="ml-3 flex-1">
                {notification.title && (
                  <h4 className={`text-sm font-medium ${colors.title}`}>
                    {notification.title}
                  </h4>
                )}
                
                <p className={`text-sm ${colors.message} ${notification.title ? 'mt-1' : ''}`}>
                  {notification.message}
                </p>
                
                {/* Azioni */}
                {notification.actions && notification.actions.length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    {notification.actions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (action.action) {
                            action.action();
                          }
                          if (onAction) {
                            onAction(notification.id, action);
                          }
                        }}
                        className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${
                          notification.type === NOTIFICATION_TYPES.ERROR
                            ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30'
                            : notification.type === NOTIFICATION_TYPES.WARNING
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:hover:bg-yellow-900/30'
                            : notification.type === NOTIFICATION_TYPES.SUCCESS
                            ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/30'
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30'
                        }`}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Pulsante di chiusura */}
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => onRemove(notification.id)}
                  className={`inline-flex rounded-md p-1.5 transition-colors ${
                    isDark 
                      ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                  }`}
                  aria-label={t('common.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationContainer;
